import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';

import { parseJsonString } from '../helpers';
import { LogoutService } from '../logout/logout.service';
import { WindowRefService } from '../window-ref/window-ref.service';

export const STORAGE_PREFIX = new InjectionToken<string>('core-util.storage-prefix');
export const STORAGE_LOGOUT_CLEANUP_KEYS = new InjectionToken<string[] | true>('core-util.storage-logout-cleanup-keys');
export const STORAGE_ACCOUNT_SWITCH_CLEANUP_KEYS = new InjectionToken<string[] | true>(
  'core-util.storage-accountSwitch-cleanup-keys'
);
export const STORAGE_EXCEPTIONAL_KEY_REGEX = new InjectionToken<RegExp>('core-util.storage-exceptional-keys');

export enum StorageType {
  LOCAL = 'localStorage',
  SESSION = 'sessionStorage'
}

/**
 * Prepare the value to be stored in browser storage.
 * Convert `undefined` values to `null` for consistency
 * @param value
 */
function prepareStorageValue(value: unknown): string | null {
  return value === undefined ? null : JSON.stringify(value);
}

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private readonly localStorage: Storage;
  private readonly sessionStorage: Storage;
  private readonly win: Window;
  exceptionalKeyPattern: RegExp;

  constructor(
    @Optional() @Inject(STORAGE_PREFIX) private readonly prefix: string,
    @Optional() @Inject(STORAGE_LOGOUT_CLEANUP_KEYS) logoutKeysToCleanup: string[] | true,
    @Optional() @Inject(STORAGE_ACCOUNT_SWITCH_CLEANUP_KEYS) accountSwitchKeysToCleanup: string[] | true,
    @Optional() @Inject(STORAGE_EXCEPTIONAL_KEY_REGEX) exceptionalKeyPattern: RegExp,
    private readonly logoutService: LogoutService,
    { nativeWindow }: WindowRefService
  ) {
    this.win = nativeWindow;
    this.localStorage = this.win[StorageType.LOCAL];
    this.sessionStorage = this.win[StorageType.SESSION];

    this.cleanUpOnLogout(logoutKeysToCleanup);
    this.cleanUpOnAccountSwitch(accountSwitchKeysToCleanup);
    this.exceptionalKeyPattern = exceptionalKeyPattern;
  }

  public setItem(key: string, value: unknown, storageType = StorageType.LOCAL, ignorePrefix = false): boolean {
    const storage = this[storageType];

    if (!storage) {
      return false;
    }

    storage.setItem(this.getKey(key, ignorePrefix), prepareStorageValue(value));

    return true;
  }

  public getItem<T = never>(key: string, storageType = StorageType.LOCAL, ignorePrefix = false): T {
    const storage = this[storageType];

    if (!storage) {
      return null;
    }

    const item = storage ? storage.getItem(this.getKey(key, ignorePrefix)) : null;

    return parseJsonString(item);
  }

  public removeItem(key: string, storageType = StorageType.LOCAL, ignorePrefix = false): boolean {
    const storage = this[storageType];

    if (!storage) {
      return false;
    }

    storage.removeItem(this.getKey(key, ignorePrefix));

    return true;
  }

  public bulkRemove(keys: string[], storageType = StorageType.LOCAL): void {
    const storage = this[storageType];

    if (!storage) {
      return;
    }

    keys.forEach(key => this.removeItem(key, storageType));
  }

  /**
   * Remove all keys having a specific prefix. If prefix is not applied,
   * then prefix configured via InjectionToken will be used.
   * @param prefix
   * @param storageType
   */
  public bulkRemovePrefixed(prefix = this.prefix, storageType = StorageType.LOCAL): void {
    const storage = this[storageType];

    if (!storage) {
      return;
    }

    this.getAllPrefixedKeys(storage, prefix).forEach(key => storage.removeItem(key));
  }

  public hasItem(key: string, storageType = StorageType.LOCAL): boolean {
    const storage = this[storageType];

    if (!storage) {
      return false;
    }

    return storage.getItem(this.getPrefixedKey(key)) !== null;
  }

  /**
   * Remove storage keys on logout (no matter if session or local storage), when
   * `STORAGE_LOGOUT_CLEANUP_KEYS` Injection token is configured in NgModule imports.
   * Either an Array of keys can be applied, or true. If true, then `STORAGE_PREFIX`
   * has to be configured, by importing `StorageModule.forChild('some_prefix', true)`,
   * as only keys starting with this prefix will be removed.
   * Some Keys can also be avoided from being cleaned up by using a specific regex pattern
   *
   * @param keysToCleanup
   * @private
   */
  private cleanUpOnLogout(keysToCleanup: string[] | true): void {
    if (keysToCleanup) {
      this.logoutService.logout$().subscribe(_ => this.cleanupKeys(keysToCleanup));
    }
  }

  /**
   * Same as above but on account switch, if `STORAGE_LOGOUT_CLEANUP_KEYS` Injection token
   * is configured in NgModule imports.
   *
   * @param keysToCleanup
   * @private
   */
  private cleanUpOnAccountSwitch(keysToCleanup: string[] | true): void {
    if (keysToCleanup) {
      this.logoutService.switchAccount$().subscribe(_ => this.cleanupKeys(keysToCleanup));
    }
  }

  private cleanupKeys(keysToCleanup: string[] | true): void {
    [this.localStorage, this.sessionStorage]
      .filter(storage => !!storage)
      .forEach(storage => {
        let keys = Array.isArray(keysToCleanup)
          ? keysToCleanup.map(key => this.getPrefixedKey(key))
          : this.getAllPrefixedKeys(storage);

        if (this.exceptionalKeyPattern) {
          keys = keys.filter(key => !this.exceptionalKeyPattern.test(key));
        }

        keys.forEach(key => {
          storage.removeItem(key);
        });
      });
  }

  private getKey(key: string, ignorePrefix: boolean): string {
    return ignorePrefix ? key : this.getPrefixedKey(key);
  }

  private getPrefixedKey(key: string): string {
    return this.prefix ? `${this.prefix}-${key}` : key;
  }

  private getAllPrefixedKeys(storage: Storage, prefix = this.prefix): string[] {
    return storage && prefix ? Object.keys(storage).filter(key => key.startsWith(prefix)) : [];
  }
}
